/**
 * Sdílené UI pro seznamové sekce (Nabídky / Objednávky / Faktury / Výroba).
 *
 * Jednotný toolbar (hledání, autor, stav, období, reset), jednotné karty
 * řádků s akcemi, stránkování, kompaktní režim a přepínač seznam/kanban.
 * Každá sekce si nechává vlastní načítání dat a detailové pohledy; tento
 * modul drží stav filtrů, kreslí skořápku sekce a společné prvky.
 */
import { icon } from '../icons.js';

export const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:1234/api'
  : '/api';

export function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** customerData může být string (UI) nebo objekt (MCP/AI) – vrátí víceřádkový text. */
export function customerDataText(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    const c = data;
    const lines = [];
    if (c.name) lines.push(c.name);
    if (c.contactPerson && c.contactPerson !== c.name) lines.push(c.contactPerson);
    if (c.address) lines.push(c.address);
    const cityLine = [c.zip, c.city].filter(Boolean).join(' ');
    if (cityLine) lines.push(cityLine);
    if (c.country && c.country !== 'Česká republika') lines.push(c.country);
    const contact = [c.phone, c.email].filter(Boolean).join(', ');
    if (contact) lines.push(contact);
    const taxLine = [c.ico ? `IČ: ${c.ico}` : '', c.dic ? `DIČ: ${c.dic}` : ''].filter(Boolean).join(', ');
    if (taxLine) {
      lines.push('');
      lines.push(taxLine);
    }
    return lines.join('\n');
  }
  return String(data);
}

/** Název klienta: preferuje serverové `customerName` (list projekce), jinak 1. řádek customerData. */
export function customerNameOf(entity) {
  if (!entity) return '';
  if (entity.customerName) return String(entity.customerName).trim();
  return (customerDataText(entity.customerData).split('\n')[0] || '').trim();
}

export function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatMoney(value, currency = 'Kč') {
  const n = typeof value === 'number' ? value : 0;
  return `${n.toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} ${currency}`;
}

/**
 * Jediný zdroj pravdy pro stavy entit (label + barvy badge).
 * Výroba používá stejné klíče jako objednávky s výrobní terminologií.
 */
export const STATUS_META = {
  offer: {
    draft:       { label: 'Rozpracovaná', badge: 'bg-gray-100 text-gray-800',       dot: 'bg-gray-500' },
    sent:        { label: 'Odeslána',     badge: 'bg-blue-100 text-blue-800',       dot: 'bg-blue-500' },
    negotiation: { label: 'Vyjednávání',  badge: 'bg-amber-100 text-amber-800',     dot: 'bg-amber-500' },
    approved:    { label: 'Schválena',    badge: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
    rejected:    { label: 'Odmítnuta',    badge: 'bg-red-100 text-red-800',         dot: 'bg-red-500' },
    converted:   { label: 'Převedena',    badge: 'bg-purple-100 text-purple-800',   dot: 'bg-purple-500' }
  },
  order: {
    received:    { label: 'Přijato',        badge: 'bg-blue-100 text-blue-800',       dot: 'bg-blue-500' },
    in_progress: { label: 'Zpracovává se',  badge: 'bg-amber-100 text-amber-800',     dot: 'bg-amber-500' },
    done:        { label: 'Hotovo',         badge: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
    to_invoice:  { label: 'Fakturovat',     badge: 'bg-purple-100 text-purple-800',   dot: 'bg-purple-500' },
    invoiced:    { label: 'Fakturováno',    badge: 'bg-indigo-100 text-indigo-800',   dot: 'bg-indigo-500' },
    delivered:   { label: 'Předáno',        badge: 'bg-gray-100 text-gray-700',       dot: 'bg-gray-500' },
    cancelled:   { label: 'Zrušeno',        badge: 'bg-red-100 text-red-800',         dot: 'bg-red-500' }
  },
  invoice: {
    draft:          { label: 'Rozpracovaná',       badge: 'bg-gray-100 text-gray-800',       dot: 'bg-gray-500' },
    issued:         { label: 'Vystavena',          badge: 'bg-blue-100 text-blue-800',       dot: 'bg-blue-500' },
    partially_paid: { label: 'Částečně uhrazena',  badge: 'bg-amber-100 text-amber-800',     dot: 'bg-amber-500' },
    paid:           { label: 'Uhrazena',           badge: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
    overdue:        { label: 'Po splatnosti',      badge: 'bg-red-100 text-red-800',         dot: 'bg-red-500' },
    cancelled:      { label: 'Stornována',         badge: 'bg-gray-100 text-gray-500',       dot: 'bg-gray-400' }
  },
  // Výrobní pohled na objednávky – stejné klíče, terminologie dílny.
  vyroba: {
    received:    { label: 'Přijato / Příprava',  badge: 'bg-blue-100 text-blue-800 border border-blue-200',       dot: 'bg-blue-500' },
    in_progress: { label: 'Ve výrobě',           badge: 'bg-amber-100 text-amber-800 border border-amber-200',    dot: 'bg-amber-500' },
    done:        { label: 'Hotovo (Expedice)',   badge: 'bg-emerald-100 text-emerald-800 border border-emerald-200', dot: 'bg-emerald-500' },
    to_invoice:  { label: 'K fakturaci',         badge: 'bg-purple-100 text-purple-800 border border-purple-200', dot: 'bg-purple-500' },
    invoiced:    { label: 'Vyfakturováno',       badge: 'bg-indigo-100 text-indigo-800 border border-indigo-200', dot: 'bg-indigo-500' },
    delivered:   { label: 'Odesláno / Předáno',  badge: 'bg-gray-100 text-gray-700 border border-gray-200',       dot: 'bg-gray-500' },
    cancelled:   { label: 'Zrušeno',             badge: 'bg-red-100 text-red-800 border border-red-200',          dot: 'bg-red-500' }
  }
};

/** Jednotný status badge (tečka + label). */
export function statusBadge(kind, status, extraClass = '') {
  const meta = (STATUS_META[kind] && STATUS_META[kind][status]) || { label: status || '—', badge: 'bg-gray-100 text-gray-800', dot: 'bg-gray-400' };
  return `
    <span class="inline-flex items-center text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${meta.badge} ${extraClass}">
      <span class="w-1.5 h-1.5 rounded-full mr-1.5 ${meta.dot}"></span>${escapeHtml(meta.label)}
    </span>
  `;
}

export function statusLabel(kind, status) {
  const meta = STATUS_META[kind] && STATUS_META[kind][status];
  return meta ? meta.label : (status || '');
}

/** Kruhové ghost tlačítko akce v řádku (ikona + tooltip + aria-label). */
function rowActionButton(a, id) {
  const tone = a.tone === 'danger'
    ? 'text-gray-400 hover:text-red-600 hover:bg-red-50'
    : a.tone === 'primary'
      ? 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
      : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100';
  return `
    <button type="button" data-action="${a.action}" data-id="${escapeHtml(id)}"
      class="inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${tone}"
      title="${escapeHtml(a.label)}" aria-label="${escapeHtml(a.label)}">
      ${icon(a.icon, 'w-4.5 h-4.5 w-[18px] h-[18px]')}
    </button>
  `;
}

/**
 * Jednotná karta (řádek) seznamu pro všechny sekce.
 *
 * @param {object} cfg
 *  - id            identifikátor entity (pro data-id akcí)
 *  - title         např. `Nabídka #20260709-001`
 *  - status        { kind, value } → badge
 *  - customerName  název klienta (zvýrazněný řádek)
 *  - metaLine      html/text s datem, autorem, počtem položek…
 *  - description   názvy položek (truncate)
 *  - price         hlavní částka (string, už zformátovaná)
 *  - priceNote     popisek pod částkou (např. "bez DPH")
 *  - badges        pole html stringů (extra badge – převod, po splatnosti…)
 *  - actions       [{ action, icon, label, tone }] – tooltip = label
 *  - compact       bool – kompaktní jednořádkové zobrazení
 *  - rowAttr       extra atributy na <li> (např. data-status pro kanban)
 */
export function renderListCard(cfg) {
  const badgeHtml = statusBadge(cfg.status.kind, cfg.status.value);
  const extraBadges = (cfg.badges || []).join('');
  const actionsHtml = (cfg.actions || []).map((a) => rowActionButton(a, cfg.id)).join('');

  if (cfg.compact) {
    return `
      <li class="hover:bg-gray-50 transition-colors cursor-pointer" data-row-id="${escapeHtml(cfg.id)}" ${cfg.rowAttr || ''}>
        <div class="px-4 py-2 flex items-center gap-3">
          <span class="text-sm font-medium text-gray-900 whitespace-nowrap">${cfg.title}</span>
          ${badgeHtml}
          <span class="text-sm font-medium text-gray-700 truncate min-w-0 flex-shrink" title="${escapeHtml(cfg.customerName || '')}">${escapeHtml(cfg.customerName || '—')}</span>
          <span class="text-xs text-gray-400 truncate min-w-0 flex-1 hidden md:inline">${cfg.description || ''}</span>
          ${extraBadges}
          <span class="text-sm font-semibold text-gray-900 whitespace-nowrap ml-auto">${cfg.price || ''}</span>
          <span class="flex items-center gap-0.5 flex-shrink-0">${actionsHtml}</span>
        </div>
      </li>
    `;
  }

  return `
    <li class="hover:bg-gray-50 transition-colors cursor-pointer" data-row-id="${escapeHtml(cfg.id)}" ${cfg.rowAttr || ''}>
      <div class="px-4 py-4 sm:px-6">
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="text-sm font-medium text-gray-900">${cfg.title}</p>
              ${badgeHtml}
              ${extraBadges}
            </div>
            <p class="mt-1 text-sm font-medium text-gray-800 truncate">${escapeHtml(cfg.customerName || '—')}</p>
            <div class="mt-1 flex items-center text-xs text-gray-500 flex-wrap gap-x-2">${cfg.metaLine || ''}</div>
            ${cfg.description ? `<p class="mt-1 text-sm text-gray-500 truncate">${cfg.description}</p>` : ''}
          </div>
          <div class="flex flex-col items-end gap-2 flex-shrink-0">
            <div class="text-right">
              <p class="text-base sm:text-lg font-semibold text-gray-900 whitespace-nowrap">${cfg.price || ''}</p>
              ${cfg.priceNote ? `<p class="text-xs text-gray-500">${escapeHtml(cfg.priceNote)}</p>` : ''}
            </div>
            <div class="flex items-center gap-0.5" onclick="event.stopPropagation()">${actionsHtml}</div>
          </div>
        </div>
      </div>
    </li>
  `;
}

/** Prázdný stav seznamu. */
export function renderEmptyState({ title, hint, iconName = 'inbox' }) {
  return `
    <div class="bg-white rounded-lg shadow-md p-8 text-center mt-4">
      ${icon(iconName, 'mx-auto h-12 w-12 text-gray-400 mb-4')}
      <h3 class="text-lg font-medium text-gray-900 mb-2">${escapeHtml(title)}</h3>
      ${hint ? `<p class="text-gray-500">${escapeHtml(hint)}</p>` : ''}
    </div>
  `;
}

export function renderLoading() {
  return `
    <div class="flex items-center justify-center py-16 text-gray-400">
      ${icon('spinner', 'w-8 h-8 animate-spin')}
    </div>
  `;
}

export function renderErrorState(message, retryJs) {
  return `
    <div class="bg-red-50 border border-red-200 rounded-md p-4 mt-4">
      <div class="flex">
        ${icon('exclamation-circle', 'h-5 w-5 text-red-400 flex-shrink-0')}
        <div class="ml-3">
          <h3 class="text-sm font-medium text-red-800">${escapeHtml(message)}</h3>
          ${retryJs ? `<button onclick="${escapeHtml(retryJs)}" class="mt-2 text-sm text-red-600 hover:text-red-500 underline">Zkusit znovu</button>` : ''}
        </div>
      </div>
    </div>
  `;
}

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

/**
 * Jednotný stav + skořápka seznamové sekce.
 *
 * Použití v manageru:
 *   this.view = new ListView({ key, title, subtitle, searchPlaceholder,
 *     statusKind, authorLabels: {me, all}, enableCustomerFilter, enableKanban,
 *     extraToolbar: () => html, onChange: () => this.load(), getManagerRef: 'window.xyManager' });
 *   render: this.view.renderShell(containerEl) → jednou; pak this.view.renderResults(...)
 *   fetch:  params = this.view.buildParams()
 */
export class ListView {
  constructor(cfg) {
    this.cfg = cfg;
    this.key = cfg.key;
    // Filtry
    this.search = '';
    this.status = 'all';
    this.author = 'me';
    this.timeRange = 'all';
    this.dateFrom = '';
    this.dateTo = '';
    this.customerId = '';
    this.customerName = '';
    this.showAdvanced = false;
    // Stránkování + zobrazení (perzistentní preference)
    const prefs = this._loadPrefs();
    this.pageSize = PAGE_SIZES.includes(prefs.pageSize) ? prefs.pageSize : DEFAULT_PAGE_SIZE;
    this.compact = Boolean(prefs.compact);
    this.viewMode = cfg.enableKanban && prefs.viewMode === 'kanban' ? 'kanban' : 'list';
    this.page = 0;
    this.total = 0;
    // Data pro selecty (aktualizuje manager z API odpovědi)
    this.statuses = cfg.statuses || [];
    this.authors = [];
    this._shellRendered = false;
  }

  _prefsKey() { return `tria-list-prefs-${this.key}`; }

  _loadPrefs() {
    try { return JSON.parse(localStorage.getItem(this._prefsKey()) || '{}'); } catch { return {}; }
  }

  _savePrefs() {
    try {
      localStorage.setItem(this._prefsKey(), JSON.stringify({
        pageSize: this.pageSize, compact: this.compact, viewMode: this.viewMode
      }));
    } catch { /* ignore */ }
  }

  getCurrentUser() {
    try {
      const fromApp = window.app && window.app.currentUser;
      if (fromApp) return fromApp;
      return JSON.parse(localStorage.getItem('currentUser') || 'null');
    } catch { return null; }
  }

  /** Query parametry pro list endpoint (vč. stránkování). */
  buildParams({ paginate = true } = {}) {
    const params = new URLSearchParams();
    if (this.status && this.status !== 'all') params.set('status', this.status);
    if (this.search) params.set('search', this.search);
    if (this.customerId) params.set('customerId', this.customerId);
    if (this.dateFrom) params.set('dateFrom', this.dateFrom);
    if (this.dateTo) params.set('dateTo', this.dateTo);
    this._applyAuthorParam(params);
    if (paginate) {
      params.set('limit', String(this.pageSize));
      params.set('offset', String(this.page * this.pageSize));
    }
    return params;
  }

  _applyAuthorParam(params) {
    if (!this.author || this.author === 'all') return;
    if (this.author === 'me') { params.set('userId', 'me'); return; }
    if (this.author.startsWith('name:')) { params.set('userName', this.author.slice(5)); return; }
    if (this.author.startsWith('id:')) { params.set('userId', this.author.slice(3)); return; }
    params.set('userId', this.author);
  }

  hasActiveFilters() {
    return Boolean(
      (this.status && this.status !== 'all')
      || this.search
      || this.customerId
      || (this.timeRange && this.timeRange !== 'all')
      || (this.author && this.author !== 'me')
    );
  }

  _activeAdvancedCount() {
    let n = 0;
    if (this.status && this.status !== 'all') n++;
    if (this.customerId) n++;
    if (this.timeRange && this.timeRange !== 'all') n++;
    if (this.author && this.author !== 'me') n++;
    return n;
  }

  resetFilters() {
    this.search = '';
    this.status = 'all';
    this.author = 'me';
    this.timeRange = 'all';
    this.dateFrom = '';
    this.dateTo = '';
    this.customerId = '';
    this.customerName = '';
    this.page = 0;
  }

  updateDatesByTimeRange() {
    if (this.timeRange === 'all') {
      this.dateFrom = ''; this.dateTo = '';
    } else if (this.timeRange === 'today') {
      const now = new Date().toISOString().split('T')[0];
      this.dateFrom = now; this.dateTo = now;
    } else if (this.timeRange === '7days') {
      const to = new Date(); const from = new Date();
      from.setDate(to.getDate() - 7);
      this.dateFrom = from.toISOString().split('T')[0];
      this.dateTo = to.toISOString().split('T')[0];
    } else if (this.timeRange === 'month') {
      const to = new Date(); const from = new Date();
      from.setMonth(to.getMonth() - 1);
      this.dateFrom = from.toISOString().split('T')[0];
      this.dateTo = to.toISOString().split('T')[0];
    }
    // 'custom' – data nastavuje uživatel ručně
  }

  _timeRangeDisplay() {
    if (this.timeRange === 'all') return 'Všechna data';
    if (!this.dateFrom && !this.dateTo) return 'Všechna data';
    const fmt = (isoStr) => {
      if (!isoStr) return '';
      const [y, m, d] = isoStr.split('-');
      return `${parseInt(d, 10)}. ${parseInt(m, 10)}.`;
    };
    return `${fmt(this.dateFrom)} – ${fmt(this.dateTo)}`;
  }

  _buildAuthorOptions() {
    const currentUser = this.getCurrentUser();
    const currentUserId = currentUser ? String(currentUser.id) : null;
    const meLabel = (this.cfg.authorLabels && this.cfg.authorLabels.me) || 'Moje';
    const options = [
      { value: 'me', label: meLabel },
      { value: 'all', label: 'Celá firma' }
    ];
    for (const author of this.authors) {
      let value;
      if (author.userId != null) {
        if (currentUserId && String(author.userId) === currentUserId) continue;
        value = `id:${author.userId}`;
      } else {
        if (currentUser && author.name === (currentUser.name || currentUser.username)) continue;
        value = `name:${author.name}`;
      }
      options.push({ value, label: author.name });
    }
    const known = new Set(options.map((o) => o.value));
    if (!known.has(this.author)) this.author = 'me';
    return options.map((o) => ({ ...o, selected: o.value === this.author }));
  }

  _id(suffix) { return `${this.key}-${suffix}`; }

  /**
   * Vykreslí skořápku sekce (hlavička + toolbar + panel filtrů + kontejnery
   * seznamu a stránkování) a naváže eventy. Volat jednou; při změně dat
   * pak jen renderResults().
   */
  renderShell(container) {
    const cfg = this.cfg;
    const statusOptions = [
      { value: 'all', label: 'Všechny stavy' },
      ...this.statuses.map((s) => ({ value: s, label: statusLabel(cfg.statusKind, s) || s }))
    ];
    const authorOptions = this._buildAuthorOptions();
    const advCount = this._activeAdvancedCount();

    container.innerHTML = `
      <div class="w-full">
        <div class="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-3 mb-4">
          <div class="flex-shrink-0">
            <h2 class="text-2xl font-semibold text-gray-900 mb-1">${escapeHtml(cfg.title)}</h2>
            <p class="text-gray-600 text-sm mb-0">${escapeHtml(cfg.subtitle || '')}</p>
          </div>

          <!-- JEDNOTNÝ TOOLBAR -->
          <div class="flex flex-wrap gap-2 items-center flex-1 lg:justify-end w-full">
            <div class="relative flex-1 min-w-[180px] max-w-md">
              <input type="text" id="${this._id('search')}"
                placeholder="${escapeHtml(cfg.searchPlaceholder || 'Hledat…')}"
                value="${escapeHtml(this.search)}"
                class="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <span class="absolute left-2.5 top-2.5 text-gray-400">${icon('magnifying-glass', 'w-4 h-4')}</span>
            </div>

            <select id="${this._id('author')}" title="Čí záznamy zobrazit"
              class="border border-gray-300 rounded-md py-2 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              ${authorOptions.map((o) => `<option value="${escapeHtml(o.value)}" ${o.selected ? 'selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
            </select>

            <button id="${this._id('toggle-filters')}" type="button"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
              ${icon('funnel', 'w-4 h-4 mr-1.5 text-gray-500')}
              Filtry
              <span id="${this._id('filters-badge')}" class="${advCount > 0 ? '' : 'hidden'} ml-1.5 bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">${advCount}</span>
            </button>

            <button id="${this._id('reset-filters')}" type="button" title="Zrušit všechny filtry a vrátit výchozí zobrazení"
              class="${this.hasActiveFilters() ? '' : 'hidden'} inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500">
              ${icon('x-mark', 'w-4 h-4 mr-1')}
              Reset
            </button>

            ${cfg.enableKanban ? `
              <span class="inline-flex rounded-md border border-gray-300 shadow-sm overflow-hidden" role="group" aria-label="Režim zobrazení">
                <button id="${this._id('view-list')}" type="button" title="Zobrazit jako seznam"
                  class="px-2.5 py-2 text-sm ${this.viewMode === 'list' ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-500 hover:bg-gray-50'}">
                  ${icon('list-bullet', 'w-4 h-4')}
                </button>
                <button id="${this._id('view-kanban')}" type="button" title="Zobrazit jako kanban podle stavů"
                  class="px-2.5 py-2 text-sm border-l border-gray-300 ${this.viewMode === 'kanban' ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-500 hover:bg-gray-50'}">
                  ${icon('view-columns', 'w-4 h-4')}
                </button>
              </span>
            ` : ''}

            <button id="${this._id('density')}" type="button" title="${this.compact ? 'Přepnout na podrobné zobrazení' : 'Přepnout na kompaktní zobrazení'}"
              class="inline-flex items-center px-2.5 py-2 border border-gray-300 shadow-sm text-sm rounded-md bg-white ${this.compact ? 'text-blue-700 bg-blue-50' : 'text-gray-500'} hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
              ${icon('bars-3', 'w-4 h-4')}
            </button>

            <button id="${this._id('refresh')}" type="button" title="Obnovit data"
              class="inline-flex items-center px-2.5 py-2 border border-gray-300 shadow-sm text-sm rounded-md text-gray-500 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500">
              ${icon('arrow-path', 'w-4 h-4')}
            </button>

            ${typeof cfg.extraToolbar === 'function' ? cfg.extraToolbar() : (cfg.extraToolbar || '')}
          </div>
        </div>

        <!-- ROZBALOVACÍ PANEL FILTRŮ -->
        <div id="${this._id('advanced-panel')}" class="${this.showAdvanced ? '' : 'hidden'} mb-4 bg-gray-50 rounded-lg border border-gray-200 p-4 shadow-inner">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            ${cfg.enableCustomerFilter !== false ? `
              <div class="relative">
                <label class="block text-xs font-semibold text-gray-600 mb-1">Firma / zákazník</label>
                <div class="relative">
                  <input type="text" id="${this._id('customer')}" placeholder="Hledat firmu…"
                    value="${escapeHtml(this.customerName)}"
                    class="w-full bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <button id="${this._id('customer-clear')}" type="button" class="${this.customerId ? '' : 'hidden'} absolute inset-y-0 right-0 pr-2.5 flex items-center text-gray-400 hover:text-gray-600">
                    ${icon('x-mark', 'w-4 h-4')}
                  </button>
                </div>
                <div id="${this._id('customer-list')}" class="hidden absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-20 max-h-40 overflow-y-auto text-sm"></div>
              </div>
            ` : ''}

            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1">Časové období</label>
              <div class="grid grid-cols-2 gap-2">
                <select id="${this._id('time')}" class="bg-white border border-gray-300 rounded-md py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="all" ${this.timeRange === 'all' ? 'selected' : ''}>Všechna data</option>
                  <option value="today" ${this.timeRange === 'today' ? 'selected' : ''}>Dnes</option>
                  <option value="7days" ${this.timeRange === '7days' ? 'selected' : ''}>Posledních 7 dní</option>
                  <option value="month" ${this.timeRange === 'month' ? 'selected' : ''}>Poslední měsíc</option>
                  <option value="custom" ${this.timeRange === 'custom' ? 'selected' : ''}>Vlastní rozmezí…</option>
                </select>
                <div id="${this._id('time-display')}" class="text-xs text-gray-500 flex items-center justify-center bg-white border border-gray-200 rounded px-2 text-center truncate">
                  ${this._timeRangeDisplay()}
                </div>
              </div>
            </div>

            <div>
              <label class="block text-xs font-semibold text-gray-600 mb-1">Stav</label>
              <select id="${this._id('status')}" class="w-full bg-white border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                ${statusOptions.map((o) => `<option value="${o.value}" ${o.value === this.status ? 'selected' : ''}>${escapeHtml(o.label)}</option>`).join('')}
              </select>
            </div>

            <div id="${this._id('date-range')}" class="${this.timeRange === 'custom' ? '' : 'hidden'} md:col-span-3 bg-white p-3 rounded-md border border-gray-200 flex flex-wrap gap-4 items-center">
              <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-600">Od:</span>
                <input type="date" id="${this._id('date-from')}" value="${this.dateFrom}" class="bg-white border border-gray-300 rounded-md py-1 px-2 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none">
              </div>
              <div class="flex items-center gap-2">
                <span class="text-xs font-medium text-gray-600">Do:</span>
                <input type="date" id="${this._id('date-to')}" value="${this.dateTo}" class="bg-white border border-gray-300 rounded-md py-1 px-2 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none">
              </div>
            </div>
          </div>

          <div class="flex justify-between items-center border-t border-gray-200 pt-3 mt-4">
            <button id="${this._id('clear-all')}" type="button" class="text-xs text-red-600 hover:text-red-800 font-medium">Vyčistit všechny filtry</button>
            <button id="${this._id('close-panel')}" type="button" class="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-1 px-3 rounded text-xs transition-colors">Zavřít</button>
          </div>
        </div>

        ${cfg.summarySlot ? `<div id="${this._id('summary')}"></div>` : ''}
        <div id="${this._id('list')}">${renderLoading()}</div>
        <div id="${this._id('pagination')}"></div>
      </div>
    `;

    this._bindShellEvents(container);
    this._shellRendered = true;
  }

  get listContainer() { return document.getElementById(this._id('list')); }
  get summaryContainer() { return document.getElementById(this._id('summary')); }

  _emitChange() {
    this._savePrefs();
    this._refreshToolbarState();
    if (typeof this.cfg.onChange === 'function') this.cfg.onChange();
  }

  /** Aktualizace badge počtu filtrů + viditelnosti resetu bez překreslení shellu. */
  _refreshToolbarState() {
    const badge = document.getElementById(this._id('filters-badge'));
    const advCount = this._activeAdvancedCount();
    if (badge) {
      badge.textContent = advCount;
      badge.classList.toggle('hidden', advCount === 0);
    }
    const reset = document.getElementById(this._id('reset-filters'));
    if (reset) reset.classList.toggle('hidden', !this.hasActiveFilters());
    const timeDisplay = document.getElementById(this._id('time-display'));
    if (timeDisplay) timeDisplay.textContent = this._timeRangeDisplay();
  }

  _bindShellEvents(container) {
    // Hledání s debounce
    const searchInput = document.getElementById(this._id('search'));
    if (searchInput) {
      let t;
      searchInput.addEventListener('input', (e) => {
        clearTimeout(t);
        t = setTimeout(() => {
          this.search = e.target.value;
          this.page = 0;
          this._emitChange();
        }, 250);
      });
    }

    document.getElementById(this._id('author'))?.addEventListener('change', (e) => {
      this.author = e.target.value;
      this.page = 0;
      this._emitChange();
    });

    document.getElementById(this._id('toggle-filters'))?.addEventListener('click', () => {
      this.showAdvanced = !this.showAdvanced;
      document.getElementById(this._id('advanced-panel'))?.classList.toggle('hidden', !this.showAdvanced);
    });

    document.getElementById(this._id('close-panel'))?.addEventListener('click', () => {
      this.showAdvanced = false;
      document.getElementById(this._id('advanced-panel'))?.classList.add('hidden');
    });

    const doReset = () => {
      this.resetFilters();
      // Synchronizace ovládacích prvků
      const set = (suffix, value) => {
        const el = document.getElementById(this._id(suffix));
        if (el) el.value = value;
      };
      set('search', ''); set('status', 'all'); set('author', 'me'); set('time', 'all');
      set('date-from', ''); set('date-to', '');
      if (this.cfg.enableCustomerFilter !== false) {
        set('customer', '');
        document.getElementById(this._id('customer-clear'))?.classList.add('hidden');
        const dd = document.getElementById(this._id('customer-list'));
        if (dd) { dd.classList.add('hidden'); dd.innerHTML = ''; }
      }
      document.getElementById(this._id('date-range'))?.classList.add('hidden');
      this._emitChange();
    };
    document.getElementById(this._id('reset-filters'))?.addEventListener('click', doReset);
    document.getElementById(this._id('clear-all'))?.addEventListener('click', doReset);

    document.getElementById(this._id('refresh'))?.addEventListener('click', () => this._emitChange());

    document.getElementById(this._id('status'))?.addEventListener('change', (e) => {
      this.status = e.target.value;
      this.page = 0;
      this._emitChange();
    });

    document.getElementById(this._id('time'))?.addEventListener('change', (e) => {
      this.timeRange = e.target.value;
      this.updateDatesByTimeRange();
      document.getElementById(this._id('date-range'))?.classList.toggle('hidden', this.timeRange !== 'custom');
      const set = (suffix, value) => {
        const el = document.getElementById(this._id(suffix));
        if (el) el.value = value;
      };
      set('date-from', this.dateFrom); set('date-to', this.dateTo);
      this.page = 0;
      this._emitChange();
    });

    document.getElementById(this._id('date-from'))?.addEventListener('change', (e) => {
      this.dateFrom = e.target.value; this.page = 0; this._emitChange();
    });
    document.getElementById(this._id('date-to'))?.addEventListener('change', (e) => {
      this.dateTo = e.target.value; this.page = 0; this._emitChange();
    });

    // Kompaktní zobrazení
    document.getElementById(this._id('density'))?.addEventListener('click', (e) => {
      this.compact = !this.compact;
      const btn = e.currentTarget;
      btn.classList.toggle('text-blue-700', this.compact);
      btn.classList.toggle('bg-blue-50', this.compact);
      btn.classList.toggle('text-gray-500', !this.compact);
      btn.title = this.compact ? 'Přepnout na podrobné zobrazení' : 'Přepnout na kompaktní zobrazení';
      this._emitChange();
    });

    // Přepínač seznam/kanban
    if (this.cfg.enableKanban) {
      const setMode = (mode) => {
        if (this.viewMode === mode) return;
        this.viewMode = mode;
        const listBtn = document.getElementById(this._id('view-list'));
        const kanbanBtn = document.getElementById(this._id('view-kanban'));
        for (const [btn, active] of [[listBtn, mode === 'list'], [kanbanBtn, mode === 'kanban']]) {
          if (!btn) continue;
          btn.classList.toggle('bg-blue-50', active);
          btn.classList.toggle('text-blue-700', active);
          btn.classList.toggle('bg-white', !active);
          btn.classList.toggle('text-gray-500', !active);
        }
        this.page = 0;
        this._emitChange();
      };
      document.getElementById(this._id('view-list'))?.addEventListener('click', () => setMode('list'));
      document.getElementById(this._id('view-kanban'))?.addEventListener('click', () => setMode('kanban'));
    }

    // Autocomplete zákazníka
    if (this.cfg.enableCustomerFilter !== false) this._bindCustomerAutocomplete();

    // Stránkování (delegace – tlačítka se překreslují)
    document.getElementById(this._id('pagination'))?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-page]');
      if (!btn) return;
      const page = parseInt(btn.dataset.page, 10);
      if (!Number.isFinite(page) || page < 0) return;
      this.page = page;
      this._emitChange();
    });
    document.getElementById(this._id('pagination'))?.addEventListener('change', (e) => {
      if (e.target.matches('[data-page-size]')) {
        this.pageSize = parseInt(e.target.value, 10) || DEFAULT_PAGE_SIZE;
        this.page = 0;
        this._emitChange();
      }
    });
  }

  _bindCustomerAutocomplete() {
    const input = document.getElementById(this._id('customer'));
    const dropdown = document.getElementById(this._id('customer-list'));
    const clearBtn = document.getElementById(this._id('customer-clear'));
    if (!input || !dropdown) return;

    dropdown.addEventListener('click', (e) => {
      const item = e.target.closest('[data-id]');
      if (!item) return;
      this.customerId = item.dataset.id;
      this.customerName = item.dataset.name;
      dropdown.classList.add('hidden');
      dropdown.innerHTML = '';
      clearBtn?.classList.remove('hidden');
      input.value = item.dataset.name;
      this.page = 0;
      this._emitChange();
    });

    clearBtn?.addEventListener('click', () => {
      this.customerId = '';
      this.customerName = '';
      input.value = '';
      dropdown.classList.add('hidden');
      dropdown.innerHTML = '';
      clearBtn.classList.add('hidden');
      this.page = 0;
      this._emitChange();
    });

    let t;
    input.addEventListener('input', (e) => {
      const value = e.target.value.trim();
      clearTimeout(t);
      if (this.customerId && value !== this.customerName) {
        this.customerId = '';
        this.customerName = '';
        clearBtn?.classList.add('hidden');
        this._emitChange();
      }
      if (value.length < 2) {
        dropdown.classList.add('hidden');
        dropdown.innerHTML = '';
        return;
      }
      t = setTimeout(async () => {
        try {
          if (!window.customersManager) return;
          const customers = await window.customersManager.fetchCustomers(value);
          dropdown.innerHTML = customers.length === 0
            ? '<div class="p-2 text-gray-500 italic text-xs">Žádný zákazník nenalezen</div>'
            : customers.map((c) => `
                <div class="px-3 py-2 hover:bg-blue-50 cursor-pointer text-gray-800 transition-colors" data-id="${c.id}" data-name="${escapeHtml(c.name)}">
                  <span class="font-medium">${escapeHtml(c.name)}</span>
                  ${c.ico ? `<span class="text-xs text-gray-400 ml-1">(IČO: ${escapeHtml(c.ico)})</span>` : ''}
                </div>
              `).join('');
          dropdown.classList.remove('hidden');
        } catch (err) {
          console.error('Chyba autocomplete:', err);
        }
      }, 200);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && dropdown.innerHTML) dropdown.classList.remove('hidden');
    });

    document.addEventListener('click', (e) => {
      if (!input.contains(e.target) && !dropdown.contains(e.target)) dropdown.classList.add('hidden');
    });
  }

  /**
   * Vykreslí výsledky (list mód): pole hotových <li> html + total z API.
   * @param {object} p { rowsHtml: string[], total: number, emptyState?: {title, hint} }
   */
  renderResults({ rowsHtml, total, emptyState }) {
    this.total = typeof total === 'number' ? total : rowsHtml.length;
    const list = this.listContainer;
    if (!list) return;

    if (!rowsHtml.length) {
      const empty = emptyState || {};
      list.innerHTML = renderEmptyState({
        title: this.hasActiveFilters()
          ? 'Nic neodpovídá zadaným filtrům'
          : (empty.title || 'Zatím tu nic není'),
        hint: this.hasActiveFilters()
          ? 'Zkuste změnit filtry nebo je resetujte tlačítkem Reset.'
          : (empty.hint || '')
      });
    } else {
      list.innerHTML = `
        <div class="bg-white shadow overflow-hidden sm:rounded-md">
          <ul class="divide-y divide-gray-200">${rowsHtml.join('')}</ul>
        </div>
      `;
    }
    this._renderPagination();
    this._refreshToolbarState();
  }

  /** Vloží do list kontejneru libovolné HTML (kanban, chyba…). */
  renderCustom(html, { showPagination = false } = {}) {
    const list = this.listContainer;
    if (!list) return;
    list.innerHTML = html;
    if (showPagination) this._renderPagination();
    else {
      const p = document.getElementById(this._id('pagination'));
      if (p) p.innerHTML = '';
    }
    this._refreshToolbarState();
  }

  renderLoading() {
    const list = this.listContainer;
    if (list) list.innerHTML = renderLoading();
  }

  _renderPagination() {
    const el = document.getElementById(this._id('pagination'));
    if (!el) return;
    const totalPages = Math.max(Math.ceil(this.total / this.pageSize), 1);
    if (this.page >= totalPages) this.page = totalPages - 1;
    const from = this.total === 0 ? 0 : this.page * this.pageSize + 1;
    const to = Math.min((this.page + 1) * this.pageSize, this.total);

    el.innerHTML = `
      <div class="flex flex-wrap items-center justify-between gap-3 py-3 text-sm text-gray-600">
        <div class="flex items-center gap-2">
          <span>${from}–${to} z ${this.total}</span>
          <select data-page-size class="border border-gray-300 rounded-md py-1 px-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" title="Počet záznamů na stránku">
            ${PAGE_SIZES.map((s) => `<option value="${s}" ${s === this.pageSize ? 'selected' : ''}>${s} / stránka</option>`).join('')}
          </select>
        </div>
        ${totalPages > 1 ? `
          <div class="flex items-center gap-1">
            <button type="button" data-page="${this.page - 1}" ${this.page === 0 ? 'disabled' : ''}
              class="inline-flex items-center px-2 py-1.5 border border-gray-300 rounded-md bg-white ${this.page === 0 ? 'text-gray-300 cursor-default' : 'text-gray-600 hover:bg-gray-50'}"
              title="Předchozí stránka">${icon('chevron-left', 'w-4 h-4')}</button>
            ${this._pageButtons(totalPages)}
            <button type="button" data-page="${this.page + 1}" ${this.page >= totalPages - 1 ? 'disabled' : ''}
              class="inline-flex items-center px-2 py-1.5 border border-gray-300 rounded-md bg-white ${this.page >= totalPages - 1 ? 'text-gray-300 cursor-default' : 'text-gray-600 hover:bg-gray-50'}"
              title="Další stránka">${icon('chevron-right', 'w-4 h-4')}</button>
          </div>
        ` : ''}
      </div>
    `;
  }

  _pageButtons(totalPages) {
    // Zobrazíme max 7 tlačítek: 1 … p-1 p p+1 … N
    const pages = new Set([0, totalPages - 1, this.page - 1, this.page, this.page + 1]);
    const sorted = [...pages].filter((p) => p >= 0 && p < totalPages).sort((a, b) => a - b);
    let html = '';
    let prev = -1;
    for (const p of sorted) {
      if (prev >= 0 && p - prev > 1) html += '<span class="px-1 text-gray-400">…</span>';
      html += `
        <button type="button" data-page="${p}"
          class="min-w-[32px] px-2 py-1.5 border rounded-md text-center ${p === this.page
            ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
            : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}">${p + 1}</button>
      `;
      prev = p;
    }
    return html;
  }
}
